import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { adminClaimService, type ClaimFilters } from '@/services/api/adminClaimService';
import type { Claim, ClaimUpdateData } from '@/types/claim';

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export const useAdminClaimStore = defineStore('adminClaim', () => {
  // State
  const claims = ref<Claim[]>([]);
  const selectedClaim = ref<Claim | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  const filters = ref<ClaimFilters>({
    search: '',
    status: '',
    brand: undefined,
    date_from: '',
    date_to: '',
    page: 1,
    per_page: 10
  });

  const pagination = ref<PaginationMeta>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  // Getters
  const underReviewClaims = computed(() => 
    claims.value.filter(c => c.claim_status === 'under review')
  );

  const approvedClaims = computed(() => 
    claims.value.filter(c => c.claim_status === 'approved')
  );

  const rejectedClaims = computed(() => 
    claims.value.filter(c => c.claim_status === 'rejected')
  );

  // Actions
  async function fetchClaims() {
    loading.value = true;
    error.value = null;

    try {
      const response = await adminClaimService.getClaims(filters.value);
      claims.value = response.data;
      pagination.value = response.meta;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch claims';
      console.error('Error fetching claims:', err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchClaim(claimId: number) {
    loading.value = true;
    error.value = null;

    try {
      const response = await adminClaimService.getClaim(claimId);
      selectedClaim.value = response.data || null;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch claim';
      console.error('Error fetching claim:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function approveClaim(claimId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminClaimService.updateClaimStatus(claimId, { status: 'approved' });
      
      // Refresh the claim list
      await fetchClaims();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to approve claim';
      console.error('Error approving claim:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function rejectClaim(claimId: number, rejectionReason?: string) {
    loading.value = true;
    error.value = null;

    try {
      await adminClaimService.updateClaimStatus(claimId, {
        status: 'rejected',
        rejection_reason: rejectionReason
      });

      // Refresh the claim list
      await fetchClaims();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to reject claim';
      console.error('Error rejecting claim:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateClaim(claimId: number, data: ClaimUpdateData) {
    loading.value = true;
    error.value = null;

    try {
      await adminClaimService.updateClaim(claimId, data);

      // Refresh the claim list
      await fetchClaims();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update claim';
      console.error('Error updating claim:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteClaim(claimId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminClaimService.deleteClaim(claimId);

      // Refresh the claim list
      await fetchClaims();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete claim';
      console.error('Error deleting claim:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function setFilters(newFilters: Partial<ClaimFilters>) {
    filters.value = { ...filters.value, ...newFilters };

    // Ensure per_page is always set to a valid value
    if (!filters.value.per_page || filters.value.per_page <= 0) {
      filters.value.per_page = 10;
    }

    // Ensure page is always set to a valid value
    if (!filters.value.page || filters.value.page <= 0) {
      filters.value.page = 1;
    }
  }

  function resetFilters() {
    filters.value = {
      search: '',
      status: '',
      brand: undefined,
      date_from: '',
      date_to: '',
      page: 1,
      per_page: 10
    };
  }

  function clearError() {
    error.value = null;
  }

  async function exportClaims() {
    loading.value = true;
    error.value = null;

    try {
      // Import XLSX dynamically
      const XLSX = await import('xlsx');

      // Fetch ALL filtered claims (no pagination)
      const allClaims = await adminClaimService.getClaimsForExport(filters.value);

      if (allClaims.length === 0) {
        throw new Error('No claims to export with current filters');
      }

      // Transform data for export
      const exportData = allClaims.map(claim => ({
        "Claim Number": claim.claim_number || '',
        "Customer Name": claim.user ? `${claim.user.first_name || ''} ${claim.user.last_name || ''}`.trim() : '',
        "Customer Email": claim.user?.email || '',
        "Brand": claim.brand?.brand_name || '',
        "Tire Pattern": claim.tire?.pattern || '',
        "Tire Size": claim.tire?.tire_size || '',
        "DOT": claim.tire?.dot || '',
        "Shop": claim.tire?.shop || '',
        "Code": claim.tire?.code || '',
        "Invoice Number": claim.tire?.invoice_number || '',
        "Vehicle": claim.vehicle ? `${claim.vehicle.vehicle_make || ''} ${claim.vehicle.vehicle_model || ''}`.trim() : '',
        "Vehicle Year": claim.vehicle?.vehicle_year || '',
        "Plate Number": claim.vehicle?.plate_number || '',
        "Dealer": claim.dealer?.dealer_name || '',
        "Date Submitted": claim.created_at ? new Date(claim.created_at).toLocaleDateString() : '',
        "Status": claim.claim_status || '',
        "Video Call Schedule": claim.videocall_schedule || '',
        "Rejection Reason": claim.rejection_reason || ''
      }));

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `claims_export_${timestamp}.xlsx`;

      // Trigger download
      XLSX.writeFile(workbook, filename);

      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to export claims';
      console.error('Error exporting claims:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    claims,
    selectedClaim,
    loading,
    error,
    filters,
    pagination,
    // Getters
    underReviewClaims,
    approvedClaims,
    rejectedClaims,
    // Actions
    fetchClaims,
    fetchClaim,
    approveClaim,
    rejectClaim,
    updateClaim,
    deleteClaim,
    setFilters,
    resetFilters,
    clearError,
    exportClaims
  };
});


